import React, { useState, useEffect } from 'react';
import { Users, Search, Building2, ArrowRight, CheckCircle, Shield, Sparkles } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getOrganizationProfile } from '../../lib/supabase/organization';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CompanySetupForm from '../../components/chat/CompanySetupForm';
import ContactForm from '../../components/operations/contacts/ContactForm';
import ContactList from '../../components/operations/contacts/ContactList';

export default function CustomersHub() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    checkProfile();
  }, []);

  const checkProfile = async () => {
    try {
      setIsLoading(true);
      const profile = await getOrganizationProfile();
      setHasProfile(!!profile);
    } catch (error) {
      console.error('Failed to check profile:', error);
      setHasProfile(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddContact = () => {
    if (!user) {
      navigate('/contact-manager/login');
      return;
    }
    
    if (!hasProfile) {
      setShowCompanyForm(true);
    } else {
      setShowContactForm(true);
    }
  };

  const handleCompanySetupComplete = () => {
    setShowCompanyForm(false);
    setHasProfile(true);
    setShowContactForm(true);
  };

  const handleSkipSetup = () => {
    setShowCompanyForm(false);
    setShowContactForm(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <Header department="Contact Manager" />
      
      <main className="pt-24 px-4">
        <div className="max-w-7xl mx-auto mb-6">
          <button 
            onClick={handleAddContact}
            className="flex items-center justify-center space-x-2 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Users className="w-5 h-5" />
            <span>Add New Customer</span>
          </button>
        </div>

        <div className="max-w-7xl mx-auto">
          {/* Contact List */}
          <ContactList 
            refreshTrigger={refreshTrigger} 
            defaultType="customer"
            showOnlyType="customer"
          />

          {/* Welcome Section */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Customer Hub</h1>
            </div>
            
            <p className="text-lg text-gray-700 mb-6">
              Hi {user?.name || 'there'}, I'm your Contact Management Assistant. I'll help you keep your contacts organized and up-to-date.
              Let's get started by adding your first contact or importing your existing contacts.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow p-6 hover:shadow-md transition-shadow">
              <Search className="w-6 h-6 text-blue-500 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Search Contacts</h3>
              <p className="text-gray-600 text-sm">
                Quickly find any contact with our powerful search.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow p-6 hover:shadow-md transition-shadow">
              <Building2 className="w-6 h-6 text-blue-500 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Organizations</h3>
              <p className="text-gray-600 text-sm">
                View and manage company relationships.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow p-6 hover:shadow-md transition-shadow">
              <Shield className="w-6 h-6 text-blue-500 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Recent Activity</h3>
              <p className="text-gray-600 text-sm">
                See your latest contact updates and changes.
              </p>
            </div>
          </div>

          {/* AI Assistant Section */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg p-8 text-white">
            <div className="flex items-center space-x-4 mb-6">
              <Sparkles className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Your AI Contact Assistant</h2>
            </div>
            <p className="text-lg mb-6">
              I can help you with:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5" />
                <span>Contact organization and cleanup</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5" />
                <span>Finding duplicate contacts</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5" />
                <span>Updating contact information</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5" />
                <span>Contact relationship insights</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {showCompanyForm && (
        <CompanySetupForm
          onComplete={handleCompanySetupComplete}
          onSkip={handleSkipSetup}
        />
      )}
      
      {showContactForm && (
        <ContactForm
          onClose={() => setShowContactForm(false)}
          defaultType="customer"
          onSuccess={() => {
            setShowContactForm(false);
            setRefreshTrigger(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
}